import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Package } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { Product } from '@tagadapay/crm-entities';
import { DateDisplay } from '@/components/date-display';

const stripHtmlTags = (html: string): string => html.replace(/<[^>]*>/g, '');

interface ProductDetailsTabProps {
  product: Product.ProductWithRelations;
  showInfo?: boolean;
  showSettings?: boolean;
}

export const ProductDetailsTab = ({
  product,
  showInfo = true,
  showSettings = true,
}: ProductDetailsTabProps) => {
  const { t } = useTranslation('products');

  if (!showInfo && !showSettings) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('productDetails')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {showInfo && (
          <div>
            <h3 className="mb-3 text-sm font-semibold">{t('information')}</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('unitLabel')}</p>
                <p className="text-sm">{product.unitLabel || '-'}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('description')}</p>
                <p className="text-sm">{product.description ? stripHtmlTags(product.description) : '-'}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('accountingCode')}</p>
                <p className="text-sm">{product.accountingCode || '-'}</p>
              </div>

              {product.externalProductId && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs font-medium">{t('externalProductId')}</p>
                  <p className="text-sm">{product.externalProductId}</p>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('variants')}</p>
                <div className="flex items-center">
                  <Package className="text-muted-foreground mr-2 h-4 w-4" />
                  <span className="text-sm">{product.variants.length}</span>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('updatedAt')}</p>
                <div className="flex items-center">
                  <Clock className="text-muted-foreground mr-2 h-4 w-4" />
                  <p className="text-sm">
                    <DateDisplay value={product.updatedAt} />
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSettings && (
          <div className={showInfo ? 'border-t pt-6' : ''}>
            <h3 className="mb-3 text-sm font-semibold">{t('settings')}</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('isShippable')}</p>
                <Badge variant={product.isShippable ? 'default' : 'secondary'}>
                  {product.isShippable ? t('yes') : t('no')}
                </Badge>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('isTaxable')}</p>
                <Badge variant={product.isTaxable ? 'default' : 'secondary'}>
                  {product.isTaxable ? t('yes') : t('no')}
                </Badge>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">{t('autoSync')}</p>
                <Badge variant={product.autoSync ? 'default' : 'secondary'}>
                  {product.autoSync ? t('enabled') : t('disabled')}
                </Badge>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
